import { Translation } from 'react-i18next'

import {
    segmentNameRenderer,
    sellerNameRenderer,
} from 'components/FieldRenderers/Renderers/segment'
import {
    BreadcrumbTreeRenderer,
    RankingCategoriesRenderer,
    SegmentProductNameRenderer,
    TitleRenderer,
} from 'components/FieldRenderers/Renderers/segmentProduct'
import { Field } from 'types'

import {
    createCurrencyField,
    createField,
    createNumberField,
} from './createField'
import { FieldCreatorOptions } from './localTypes'
import translations from './metrics/translations/segments'
import { asProductInfoPlaceholder } from './productMetadata'

function productInfo<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    return createField({
        ...options,
        id: 'segment_product_id',
        name: 'Product',
        shortName: 'Product',
        minWidth: options.minWidth ?? 120,
        dataIndex: options.dataIndex ?? ['product_id'],
        renderOptions: {
            render: (props) =>
                TitleRenderer<any>({
                    ...props,
                    showImage: options.showImage,
                }),
        },
        width: 300,
    })
}

export function productInfoPlaceholder<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    return asProductInfoPlaceholder(productInfo(options))
}

export function title<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    return createField({
        ...options,
        id: 'segment_product_title',
        name: 'Product',
        shortName: 'Product',
        minWidth: options.minWidth ?? 80,
        dataIndex: options.dataIndex ?? ['title'],
        renderOptions: {
            render: (props) =>
                TitleRenderer<any>({ ...props, showImage: options.showImage }),
        },
        width: 300,
        exportIndex: 7,
    })
}

export function brand<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    const dataIndex = options.dataIndex ?? ['brand']
    return createField({
        ...options,
        id: 'brand',
        name: 'Brand',
        shortName: 'Brand',
        minWidth: options.minWidth ?? 100,
        dataIndex,
        width: options.width ?? 180,
        exportIndex: 3,
    })
}

export function price<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    return createCurrencyField({
        ...options,
        id: 'segment_product_price',
        name: translations.segment_product_price.name,
        shortName: translations.segment_product_price.shortName,
        minWidth: 40,
        dataIndex: options.dataIndex ?? ['price'],
        align: 'right',
        localDefinition: translations.segment_product_price.helpText,
    })
}

export function breadcrumbTree<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    const dataIndex = options.dataIndex ?? ['breadcrumb_tree']
    return createField({
        ...options,
        id: 'breadcrumb_tree',
        name: 'Categories',
        shortName: 'Categories',
        minWidth: 50,
        dataIndex,
        width: 100,
        renderOptions: {
            render: (props) =>
                BreadcrumbTreeRenderer<any>({
                    ...props,
                    undefinedValue:
                        typeof options.emptyValue === 'function'
                            ? options.emptyValue(props.record)
                            : options.emptyValue,
                }),
        },
    })
}

export function rankingCategories<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    const dataIndex = options.dataIndex ?? ['subcategories']
    return createField({
        ...options,
        id: 'ranking_categories',
        name: 'Best Seller Rank',
        shortName: 'Best Seller Rank',
        minWidth: 100,
        dataIndex,
        renderOptions: {
            render: (props) =>
                RankingCategoriesRenderer<any>({
                    ...props,
                    undefinedValue:
                        typeof options.emptyValue === 'function'
                            ? options.emptyValue(props.record)
                            : options.emptyValue,
                }),
        },
        width: 200,
        sorter: false,
    })
}

export function estimatedRevenue<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    const dataIndex = options.dataIndex ?? ['estimated_revenue']
    const name = (
        <Translation>
            {(t) => t('segments:estimatedRevenue', '30-Day Estimated Revenue')}
        </Translation>
    )
    return createCurrencyField({
        ...options,
        id: 'segment_product_estimated_revenue',
        name,
        shortName: '30-Day Est. Revenue',
        minWidth: 50,
        dataIndex,
        width: 70,
        align: 'right',
    })
}

export function estimatedSales<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    const dataIndex = options.dataIndex ?? ['estimated_sales']
    const name = (
        <Translation>
            {(t) => t('segments:estimatedUnits', '30-Day Estimated Units')}
        </Translation>
    )
    return createNumberField({
        id: 'segment_product_estimated_sales',
        name,
        shortName: '30-Day Est. Units',
        isResizeable: true,
        minWidth: 50,
        isVisible: true,
        dataIndex,
        width: 70,
        align: 'right',
    })
}

export function productId<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    const dataIndex = options.dataIndex ?? ['product_id']
    const name = (
        <Translation>{(t) => t('common:product', 'Product')}</Translation>
    )
    return createField({
        ...options,
        id: 'product_id',
        name,
        shortName: name,
        minWidth: 100,
        dataIndex,
        renderOptions: options.renderOptions ?? {
            render: (props) =>
                SegmentProductNameRenderer<any>({ cellRenderProps: props }, [
                    'product',
                ]),
        },
        width: 300,
    })
}

export function segmentId<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    const dataIndex = options.dataIndex ?? ['segment_id']
    const name = (
        <Translation>{(t) => t('common:segment', 'Segment')}</Translation>
    )
    return createField({
        ...options,
        id: 'segment_id',
        name,
        shortName: name,
        minWidth: 120,
        dataIndex,
        renderOptions: options.renderOptions ?? {
            render: (cellRenderProps) =>
                segmentNameRenderer<any>({ cellRenderProps }, ['segment']),
        },
        width: 120,
    })
}

export function sellerName<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    const dataIndex = options.dataIndex ?? ['seller_name']
    return createField({
        ...options,
        id: 'seller_name',
        name: 'Seller Name',
        shortName: 'Seller Name',
        dataIndex,
        renderOptions: options.renderOptions ?? {
            render: (cellRenderProps) =>
                sellerNameRenderer<any>(cellRenderProps),
        },
    })
}

export function reviews<RecordType>(
    options: FieldCreatorOptions<RecordType> = {}
): Field<RecordType> {
    const name = (
        <Translation>{(t) => t('segments:reviews', 'Reviews')}</Translation>
    )
    return createNumberField({
        ...options,
        id: 'segment_product_reviews',
        name,
        shortName: 'Reviews',
        minWidth: 40,
        dataIndex: options.dataIndex ?? ['reviews'],
        align: 'right',
    })
}
